import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Table, Button, message, Image, Modal, Spin } from 'antd';
import { FileOutlined, SaveOutlined } from '@ant-design/icons';
import { BaoCaoDuAnCongViecView } from '@/types/action-plan/ActionPlan/BaoCaoDuAnCongViecView';
import { usePrivateStore } from '@/store/usePrivateStore';
import { useAuthStore } from '@/store/useAuthStore';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';
import { createUnitHelpers } from '@/components/MeasureIndicators/helpers/unitHelpers';
import { UploadModal } from '@/components/common/ActionPlan/CongViecDuAn/Right/Table/Modals';
import { tabletansuatduan, defaultTabletansuatduan } from '@/types/performance/bangtuansuatduan';
import CommonJs from '@/utils/commonJs';
import CustomTooltip from '@/components/common/Tooltip';
import { baocaoApi } from '@/services/action-plan/baocao';
import MaskedInput from '@/components/Editor/MaskedInput';
import { ColumnsType } from 'antd/es/table';
import type { ReactNode } from 'react';
import type { DuAnCongViecChiTietView, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import type { tabletansuat } from '@/types/performance/bangtuansuat';
import type { ApiResponse } from '@/types/base';
import type { RequestDTO } from '@/types/requestDTO';
import { isZeroReportWithoutNote, zeroReportNoteMessage } from '@/components/common/ActionPlan/utils/reportNote';

export interface ColumnItem {
  id?: string;
  title: string;
  date: string;
  creator: string;
  position: string;
  person: string;
  content: string;
  /** Giá trị thực tế (báo cáo dùng chung 1 bản). */
  hoanThanh?: number | null;
  chiTieu?: number | null;
  trangThai?: boolean;
  filesDinhKem?: string;
  /** Flag transient cho virtual table re-render khi dirty */
  __edited?: boolean;
}

export interface ReportTableProps {
  data?: DuAnCongViecChiTietView | null;
  active?: boolean;
  onSaveSuccess?: (payload?: { listChange?: DuAnCongViecTree[] }) => void;
  readOnly?: boolean;
  /** Action Plan V2: lưu qua `savebaocaov2` (ghi thời điểm báo cáo). */
  saveV2?: boolean;
}

export interface HoanThanhCellProps {
  rowKey: string;
  value: number | null | undefined;
  disabled?: boolean;
  onChangeDraft: (nextValue: number | null) => void;
}

/** Kỳ tần suất từ getbaocao — kèm PascalCase fallback từ BE. */
export type BaoCaoTanSuatRow = tabletansuat & {
  nguoiTao?: string;
};

export type GetBaoCaoResponse = ApiResponse<BaoCaoDuAnCongViecView>;
export type SaveBaoCaoResponse = ApiResponse<{
  saved?: boolean;
  listChange?: DuAnCongViecTree[];
}> | { success?: boolean; message?: string; data?: { saved?: boolean; listChange?: DuAnCongViecTree[] } };

export function pickListChangeFromSaveBaoCaoResponse(
  response: SaveBaoCaoResponse | null | undefined,
): DuAnCongViecTree[] {
  const payload = (response as { data?: { listChange?: DuAnCongViecTree[] } })?.data;
  if (Array.isArray(payload?.listChange) && payload.listChange.length > 0) {
    return payload.listChange;
  }
  return [];
}

export type GetBaoCaoRequest = RequestDTO;

export function parseHoanThanhDecimalInput(displayValue: string | undefined): string {
  if (!displayValue) return '';

  const cleaned = displayValue
    .replace(/\s/g, '')
    .replace(/,/g, '');

  const parts = cleaned.split('.');

  if (parts.length === 1) return cleaned;

  const integerPart = parts[0];
  const decimalPart = parts[1].slice(0, 3);

  return decimalPart ? `${integerPart}.${decimalPart}` : `${integerPart}.`;
}

export function isHoanThanhChanged(
  a: number | null | undefined,
  b: number | null | undefined
): boolean {
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;
  return Number(a) !== Number(b);
}

export function tanSuatRowKey(tanSuat: BaoCaoTanSuatRow): string {
  // Một kỳ hiển thị = một dòng (gộp Guid V1/V2). Ưu tiên tungay|title vì user thấy trùng ở đây.
  const tungay = (tanSuat.tungay || '').trim();
  const title = (tanSuat.title || '').trim();
  if (tungay || title) return `${tungay}|${title}`;
  const ketThuc =
    tanSuat.ketthuc != null ? String(tanSuat.ketthuc).slice(0, 10) : '';
  return `${ketThuc}|${tanSuat.idbaocao || tanSuat.id || ''}`;
}

/** Gộp 2 bản trùng kỳ thành 1 row — ưu tiên field đã có. */
export function mergeTanSuatSameKy(
  a: BaoCaoTanSuatRow,
  b: BaoCaoTanSuatRow
): BaoCaoTanSuatRow {
  return {
    ...a,
    ...b,
    idbaocao: a.idbaocao || b.idbaocao,
    thucTe: a.thucTe ?? b.thucTe ?? null,
    chiTieu: a.chiTieu || b.chiTieu,
    danhGia: a.danhGia || b.danhGia || '',
    filedinhkem: a.filedinhkem || b.filedinhkem || '',
  };
}

export type TheadWrapperProps = {
  className?: string;
  children?: ReactNode;
} & Record<string, unknown>;

const HoanThanhCell: React.FC<HoanThanhCellProps> = React.memo(
  ({ rowKey, value, disabled, onChangeDraft }) => {
    const [text, setText] = useState(() => (value == null ? '' : CommonJs.formatMoney(value)));

    // Luôn giữ callback mới nhất mà không cần re-render
    const onChangeDraftRef = useRef(onChangeDraft);
    onChangeDraftRef.current = onChangeDraft;

    useEffect(() => {
      setText(value == null ? '' : CommonJs.formatMoney(value));
    }, [rowKey, value]);



    return (
      <MaskedInput
        maskType="decimal"
        scale={3}
        value={text}
        disabled={disabled}
        placeholder={''}
        onChange={(value: string) => {
          const normalized = parseHoanThanhDecimalInput(value);
          setText(normalized);
          const trimmed = normalized.trim();
          if (trimmed === '' || trimmed === '.') {
            queueMicrotask(() => {
              onChangeDraftRef.current(null);
            });
            return;
          }
          const num = parseFloat(normalized);
          if (!Number.isNaN(num)) {
            queueMicrotask(() => {
              onChangeDraftRef.current(num);
            });
          }
        }}
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
          e.target.select();
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          const v = String(e.target?.value ?? '').trim();
          const trimmed = v.trim();
          if (trimmed === '' || trimmed === '.') {
            queueMicrotask(() => {
              setText('');
              onChangeDraftRef.current(null);
            });
            return;
          }
          const normalized = parseHoanThanhDecimalInput(v);
          const num = parseFloat(normalized);
          if (!Number.isNaN(num)) {
            queueMicrotask(() => {
              setText(CommonJs.formatMoney(num));
              onChangeDraftRef.current(num);
            });
          }
        }}
        style={{
          fontSize: '11px',
          fontWeight: 'bold',
          textAlign: 'right',
          width: '100%',
          borderRadius: '6px',
          height: '25px',
          backgroundColor: '#ffffff',
        }}
      />
    );
  },
  // Bỏ qua thay đổi onChangeDraft (luôn là function mới do inline closure),
  // chỉ re-render khi rowKey, value hoặc disabled thực sự thay đổi
  (prev, next) =>
    prev.rowKey === next.rowKey &&
    prev.value === next.value &&
    prev.disabled === next.disabled
);

HoanThanhCell.displayName = 'HoanThanhCell';

const ReportTable: React.FC<ReportTableProps> = ({
  data,
  active = false,
  onSaveSuccess,
  readOnly = false,
  saveV2 = false,
}) => {
  const saveBaoCaoApi = saveV2 ? baocaoApi.savebaocaov2 : baocaoApi.savebaocao;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tableData, setTableData] = useState<ColumnItem[]>([]);
  const [baoCaoData, setBaoCaoData] = useState<BaoCaoDuAnCongViecView | null>(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadModalOnlyView, setUploadModalOnlyView] = useState(false);
  const [selectedRecordForUpload, setSelectedRecordForUpload] = useState<ColumnItem | null>(null);
  const [documentFiles, setDocumentFiles] = useState<string[]>([]);
  const [savingRowIndex, setSavingRowIndex] = useState<number | null>(null);
  const [editedRowKeys, setEditedRowKeys] = useState<Set<string>>(new Set());
  const [hasChilds, setHasChilds] = useState(false);
  const [textHinhThuc, setTextHinhThuc] = useState('');

  const contentDraftRef = useRef<Record<string, string>>({});
  const hoanThanhDraftRef = useRef<Record<string, number | null>>({});
  const hasReportedRef = useRef<Record<string, boolean>>({});

  const { getPhanHe, getRequestHeader } = usePrivateStore();
  const { tenantID } = useAuthStore();
  const { units } = useLocalFilter();
  const unitHelpers = useMemo(() => createUnitHelpers(units, tenantID || ''), [units, tenantID]);

  const duAnCongViec = data?.duAnCongViec;
  const congViec = duAnCongViec?.congViec;


  const phanHe = getPhanHe() || 'buoc9';
  const idPhuTrach = congViec?.nguoiPhuTrachID || '';
  const id = congViec?.id || '';

  const getRowEditKey = useCallback((record: ColumnItem, index: number) => record.id || `row-${index}`, []);

  const findOriginalTanSuat = useCallback(
    (record: ColumnItem) =>
      baoCaoData?.listTanSuat?.find(
        (ts: BaoCaoTanSuatRow) =>
          ts.idbaocao === record.id ||
          ts.id === record.id
      ),
    [baoCaoData]
  );

  const getOriginalRowData = useCallback(
    (record: ColumnItem) => {
      const original = findOriginalTanSuat(record);
      return {
        hoanThanh: original?.thucTe ?? null,
        content: original?.danhGia ?? '',
        filesDinhKem: original?.filedinhkem ?? '',
      };
    },
    [findOriginalTanSuat]
  );

  const markRowAsEdited = useCallback(
    (
      record: ColumnItem,
      index: number,
      nextHoanThanh?: number | null,
      nextContent?: string,
      nextFilesDinhKem?: string,
    ) => {
      const rowKey = getRowEditKey(record, index);
      const original = getOriginalRowData(record);

      const currentHoanThanh =
        nextHoanThanh !== undefined
          ? nextHoanThanh
          : (hoanThanhDraftRef.current[rowKey] ?? record.hoanThanh ?? null);

      const currentContent =
        nextContent !== undefined
          ? nextContent
          : (contentDraftRef.current[rowKey] ?? record.content ?? '');

      const currentFiles =
        nextFilesDinhKem !== undefined
          ? nextFilesDinhKem
          : (record.filesDinhKem ?? '');

      const hoanThanhChanged =
        isHoanThanhChanged(currentHoanThanh, original.hoanThanh);
      const contentChanged = (currentContent ?? '') !== (original.content ?? '');
      const filesChanged = (currentFiles ?? '') !== (original.filesDinhKem ?? '');
      const rowDirty = hoanThanhChanged || contentChanged || filesChanged;

      // Sync setState — virtual table cần update thật để hiện nút Lưu (không dùng startTransition)
      setEditedRowKeys((prev) => {
        const alreadyChanged = prev.has(rowKey);
        if (rowDirty && alreadyChanged) return prev;
        if (!rowDirty && !alreadyChanged) return prev;

        const next = new Set(prev);
        if (rowDirty) next.add(rowKey);
        else next.delete(rowKey);
        return next;
      });
    },
    [getOriginalRowData, getRowEditKey]
  );

  useEffect(() => {
    if (active && id) {
      loadBaoCao();
    }
  }, [active, id, data]);

  const loadBaoCao = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const requestData: GetBaoCaoRequest & Record<string, unknown> = {
        PhanHe: phanHe,
        IDPhuTrach: idPhuTrach,
        ID: id,
        requestHeader: getRequestHeader(),
      };

      const response = (await baocaoApi.getbaocao(requestData)) as unknown as GetBaoCaoResponse;

      if (response?.success !== false) {
        const baoCao = response.data as BaoCaoDuAnCongViecView;
        setBaoCaoData(baoCao);

        setHasChilds((baoCao.childs?.length ?? 0) > 0);

        const tenHinhThucTinh = (() => {
          switch (baoCao.congViec.hinhThucTongHop) {
            case 0:
              return 'Hiệu suất tỉ trọng';
            case 1:
              return 'Báo cáo nhập tay';
            case 2:
              return 'Tổng con';
            case 3:
              return 'Trung bình con';
            default:
              return '';
          }
        })();
        setTextHinhThuc(tenHinhThucTinh);


        const reports: ColumnItem[] = [];
        const nguoiTao = baoCao.congViec?.nguoiTao || '';
        const nguoiPhuTrach = baoCao.congViec?.nguoiPhuTrachID || '';
        const tenDonVi = baoCao.congViec?.idDoLuong || '';
        const rawList = baoCao.listTanSuat || [];
        // Một kỳ báo cáo = 1 dòng.
        const mergedByKy = new Map<string, BaoCaoTanSuatRow>();
        (rawList as BaoCaoTanSuatRow[]).forEach((tanSuat) => {
          const key = tanSuatRowKey(tanSuat);
          const prev = mergedByKy.get(key);
          mergedByKy.set(key, prev ? mergeTanSuatSameKy(prev, tanSuat) : tanSuat);
        });
        const listTanSuat = Array.from(mergedByKy.values());

        listTanSuat.forEach((tanSuat) => {
          reports.push({
            id: tanSuat.idbaocao || tanSuat.id || '',
            title: tanSuat.title || '',
            date: tanSuat.tungay || '',
            creator: tanSuat.nguoiTao || nguoiTao || '',
            position: tenDonVi || '',
            person: nguoiPhuTrach || '',
            content: tanSuat.danhGia || '',
            hoanThanh: tanSuat.thucTe ?? null,
            chiTieu: tanSuat.chiTieu ?? null,
            trangThai: tanSuat.trangThai || false,
            filesDinhKem: tanSuat.filedinhkem || '',
          });
        });

        const nextHasReported: Record<string, boolean> = {};
        const nextContentDraft: Record<string, string> = {};
        const nextHoanThanhDraft: Record<string, number | null> = {};

        reports.forEach((item, index) => {
          const rowKey = item.id || `row-${index}`;
          nextHasReported[rowKey] = item.hoanThanh != null;
          nextContentDraft[rowKey] = item.content ?? '';
          nextHoanThanhDraft[rowKey] = item.hoanThanh ?? null;
        });

        hasReportedRef.current = nextHasReported;
        contentDraftRef.current = nextContentDraft;
        hoanThanhDraftRef.current = nextHoanThanhDraft;

        setTableData(reports);
        setEditedRowKeys(new Set());
      } else {
        hasReportedRef.current = {};
        contentDraftRef.current = {};
        hoanThanhDraftRef.current = {};
        setTableData([]);
        setEditedRowKeys(new Set());
      }
    } catch (error) {
      console.error('Error loading báo cáo:', error);
      message.error('Không thể tải dữ liệu báo cáo');
    } finally {
      setLoading(false);
    }
  };

  const upsertTanSuatFromRecord = useCallback(
    (
      listTanSuat: BaoCaoTanSuatRow[],
      record: ColumnItem,
      index: number,
      currentContent: string,
      options?: {
        hoanThanh?: number | null;
      },
    ) => {
      const tanSuatIndex = listTanSuat.findIndex(
        (ts) =>
          ts.idbaocao === record.id ||
          ts.id === record.id
      );

      const actualHoanThanh = options?.hoanThanh ?? record.hoanThanh ?? null;
      const actualChiTieu = record.chiTieu ?? 0;
      const actualValueForCalc = actualHoanThanh ?? 0;
      const idForVer = record.id || '';

      const applyNhapFields = (tanSuat: BaoCaoTanSuatRow) => {
        tanSuat.thucTe = actualHoanThanh;
        tanSuat.idbaocao = idForVer || tanSuat.idbaocao;
      };

      if (tanSuatIndex >= 0) {
        const tanSuat = listTanSuat[tanSuatIndex];
        applyNhapFields(tanSuat);
        tanSuat.thucTeHienThi =
          actualHoanThanh == null
            ? ''
            : actualHoanThanh.toLocaleString('vi-VN', { maximumFractionDigits: 3 });
        tanSuat.danhGia = currentContent || '';
        tanSuat.filedinhkem = record.filesDinhKem || '';
        tanSuat.trangThai = actualHoanThanh != null && actualHoanThanh >= actualChiTieu;
        tanSuat.thucTeBieuDo = actualValueForCalc;

        if (record.date) {
          const dateParts = record.date.split('/');
          if (dateParts.length === 2) {
            const currentYear = congViec?.year || new Date().getFullYear();
            const ngayDate = new Date(currentYear, parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
            tanSuat.ngay = ngayDate;
            tanSuat.ngay_Temp = ngayDate;
          }
        }
      } else {
        const dateParts = record.date ? record.date.split('/') : [];
        let ngayDate: Date = new Date();

        if (dateParts.length === 2) {
          const currentYear = congViec?.year || new Date().getFullYear();
          ngayDate = new Date(currentYear, parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
        }

        const newTanSuat: tabletansuatduan = {
          ...defaultTabletansuatduan,
          id: idForVer || record.id || '',
          idbaocao: idForVer || record.id || CommonJs.createTempID(),
          filedinhkem: record.filesDinhKem || '',
          batdau: ngayDate,
          ketthuc: ngayDate,
          chiTieu: actualChiTieu,
          thucTe: actualHoanThanh ?? null,
          thucTeHienThi:
            actualHoanThanh == null
              ? ''
              : actualHoanThanh.toLocaleString('vi-VN', { maximumFractionDigits: 3 }),
          danhGia: currentContent || '',
          thucTeBieuDo: actualValueForCalc,
          trangThai: actualHoanThanh != null && actualHoanThanh >= actualChiTieu,
          ngay_Temp: ngayDate,
          ngay: ngayDate,
        };
        applyNhapFields(newTanSuat);

        listTanSuat.push(newTanSuat);
      }
    },
    [congViec?.year]
  );

  const buildRowDrafts = useCallback((record: ColumnItem, index: number) => {
    const rowKey = getRowEditKey(record, index);
    const currentContent = contentDraftRef.current[rowKey] ?? record.content ?? '';
    const currentHoanThanh =
      hoanThanhDraftRef.current[rowKey] !== undefined
        ? hoanThanhDraftRef.current[rowKey]
        : (record.hoanThanh ?? null);

    return { rowKey, currentContent, currentHoanThanh };
  }, [getRowEditKey]);

  const saveBaoCaoRow = useCallback(
    async (
      record: ColumnItem,
      index: number,
      currentContent: string,
      currentHoanThanh: number | null | undefined,
    ) => {
      if (!baoCaoData || !congViec) return { success: false };

      const curentTanSuat: tabletansuatduan = {
        ...defaultTabletansuatduan,
        id: record.id || '',
        idbaocao: record.id || CommonJs.createTempID(),
        filedinhkem: record.filesDinhKem || '',
      };

      const listTanSuat = [curentTanSuat];
      upsertTanSuatFromRecord(listTanSuat, record, index, currentContent, {
        hoanThanh: currentHoanThanh,
      });

      const saveData: BaoCaoDuAnCongViecView = {
        ...baoCaoData,
        congViec: congViec as DuAnCongViecDTO,
        listTanSuat,
        tenDonVi: '',
      };

      return saveBaoCaoApi(saveData) as unknown as Promise<SaveBaoCaoResponse>;
    },
    [baoCaoData, congViec, upsertTanSuatFromRecord, saveBaoCaoApi]
  );

  const handleSaveRow = useCallback(
    async (record: ColumnItem, index: number) => {
      if (!baoCaoData || !congViec) {
        message.warning('Không có dữ liệu để lưu');
        return;
      }

      const { rowKey, currentContent, currentHoanThanh } =
        buildRowDrafts(record, index);
      if (isZeroReportWithoutNote(currentHoanThanh, currentContent)) {
        message.warning(zeroReportNoteMessage(record, index));
        return;
      }

      try {
        setSavingRowIndex(index);
        setSaving(true);

        const lastResponse = await saveBaoCaoRow(record, index, currentContent, currentHoanThanh);

        if (lastResponse?.success !== false) {
          message.success('Lưu báo cáo thành công');

          setBaoCaoData((prev) => {
            if (!prev) return prev;
            const updatedListTanSuat = [...(prev.listTanSuat || [])];
            upsertTanSuatFromRecord(updatedListTanSuat, record, index, currentContent, {
              hoanThanh: currentHoanThanh,
            });
            return { ...prev, listTanSuat: updatedListTanSuat };
          });

          setTableData((prev) => {
            const updated = [...prev];
            const patch: Partial<ColumnItem> = {
              content: currentContent,
              hoanThanh: currentHoanThanh,
            };
            updated[index] = { ...updated[index], ...patch };
            return updated;
          });

          hasReportedRef.current[rowKey] = currentHoanThanh != null;

          setEditedRowKeys((prev) => {
            const next = new Set(prev);
            next.delete(rowKey);
            return next;
          });

          const listChange = pickListChangeFromSaveBaoCaoResponse(lastResponse);
          onSaveSuccess?.(listChange.length > 0 ? { listChange } : undefined);
        } else {
          message.error(lastResponse?.message || 'Lưu báo cáo thất bại');
        }
      } catch (error) {
        console.error('Error saving báo cáo:', error);
        message.error('Có lỗi xảy ra khi lưu báo cáo');
      } finally {
        setSaving(false);
        setSavingRowIndex(null);
      }
    },
    [
      baoCaoData,
      buildRowDrafts,
      congViec,
      getRowEditKey,
      onSaveSuccess,
      saveBaoCaoRow,
      upsertTanSuatFromRecord,
    ]
  );

  const handleSaveAll = useCallback(async () => {
    if (!baoCaoData || !congViec) {
      message.warning('Không có dữ liệu để lưu');
      return;
    }

    // Kỳ vừa sửa báo cáo bằng 0 phải có ghi chú (như lưu ý dưới bảng).
    for (const [index, record] of tableData.entries()) {
      const { rowKey, currentContent, currentHoanThanh } = buildRowDrafts(record, index);
      if (editedRowKeys.has(rowKey) && isZeroReportWithoutNote(currentHoanThanh, currentContent)) {
        message.warning(zeroReportNoteMessage(record, index));
        return;
      }
    }

    try {
      setSavingRowIndex(null);
      setSaving(true);
      const changedRowKeys = new Set(editedRowKeys);

      const listTanSuat = [...(baoCaoData.listTanSuat || [])];
      tableData.forEach((record, index) => {
        const { currentContent, currentHoanThanh } = buildRowDrafts(record, index);
        upsertTanSuatFromRecord(listTanSuat, record, index, currentContent, {
          hoanThanh: currentHoanThanh,
        });
      });

      const response = (await saveBaoCaoApi({
        ...baoCaoData,
        congViec: congViec as DuAnCongViecDTO,
        listTanSuat,
        tenDonVi: '',
      })) as unknown as SaveBaoCaoResponse;

      if (response?.success === false) {
        message.error(response?.message || 'Lưu báo cáo thất bại');
        return;
      }

      message.success('Lưu báo cáo thành công');
      setBaoCaoData((prev) => {
        if (!prev) return prev;
        const updatedListTanSuat = [...(prev.listTanSuat || [])];
        tableData.forEach((record, index) => {
          const { currentContent, currentHoanThanh } =
            buildRowDrafts(record, index);
          upsertTanSuatFromRecord(updatedListTanSuat, record, index, currentContent, {
            hoanThanh: currentHoanThanh,
          });
        });
        return { ...prev, listTanSuat: updatedListTanSuat };
      });

      setTableData((prev) =>
        prev.map((item, index) => {
          const { currentContent, currentHoanThanh } =
            buildRowDrafts(item, index);
          return {
            ...item,
            content: currentContent,
            hoanThanh: currentHoanThanh,
          };
        })
      );

      changedRowKeys.forEach((rowKey) => {
        hasReportedRef.current[rowKey] = true;
      });

      setEditedRowKeys(new Set());
      const listChange = pickListChangeFromSaveBaoCaoResponse(response);
      onSaveSuccess?.(listChange.length > 0 ? { listChange } : undefined);
    } catch (error) {
      console.error('Error saving báo cáo (all):', error);
      message.error('Có lỗi xảy ra khi lưu báo cáo');
    } finally {
      setSaving(false);
    }
  }, [
    baoCaoData,
    buildRowDrafts,
    congViec,
    editedRowKeys,
    getRowEditKey,
    onSaveSuccess,
    saveBaoCaoApi,
    tableData,
    upsertTanSuatFromRecord,
  ]);

  const handleSaveRowRef = useRef(handleSaveRow);
  const handleSaveAllRef = useRef(handleSaveAll);

  useEffect(() => {
    handleSaveRowRef.current = handleSaveRow;
  }, [handleSaveRow]);

  useEffect(() => {
    handleSaveAllRef.current = handleSaveAll;
  }, [handleSaveAll]);

  const handleUploadModalSave = (files: string[]) => {
    if (!selectedRecordForUpload) return;

    const nextFiles = files.join(',');

    setTableData((prev) =>
      prev.map((item, index) => {
        const matched = item.id === selectedRecordForUpload.id;
        if (!matched) return item;

        const updatedItem = { ...item, filesDinhKem: nextFiles };
        markRowAsEdited(updatedItem, index, undefined, undefined, nextFiles);
        return updatedItem;
      })
    );

    setUploadModalVisible(false);
    setSelectedRecordForUpload(null);
    setDocumentFiles([]);
    message.success('Đã cập nhật tài liệu');
  };



  const renderHoanThanhInputCell = (
    value: number | null | undefined,
    record: ColumnItem,
    index: number,
    options?: { disabled?: boolean },
  ) => {
    const rowKey = getRowEditKey(record, index);
    const cellDisabled = Boolean(options?.disabled) || readOnly;
    if (hasChilds) {
      return (
        <div style={{ width: '100%', maxWidth: 110, fontSize: 11, fontWeight: 'bold', textAlign: 'center', marginTop: 3 }}>
          {CommonJs.formatMoney(record.hoanThanh ?? 0)}
        </div>
      );
    }

    return (
      <div style={{ width: '100%', maxWidth: 110 }}>
        <HoanThanhCell
          rowKey={`${rowKey}-v-p`}
          value={value ?? null}
          disabled={cellDisabled}
          onChangeDraft={(nextValue) => {
            if (cellDisabled) return;
            hoanThanhDraftRef.current[rowKey] = nextValue;
            markRowAsEdited(record, index, nextValue);
            setTableData((prev) =>
              prev.map((row, i) => {
                if (getRowEditKey(row, i) !== rowKey) return row;
                return { ...row, __edited: true, hoanThanh: nextValue };
              })
            );
          }}
        />
      </div>
    );
  };

  const renderHoanThanhResultCell = (record: ColumnItem) => {
    return (
      <CustomTooltip value="Kết quả (chỉ đọc)">
        <div style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', marginTop: 3, color: '#555' }}>
          {CommonJs.formatMoney(record.hoanThanh ?? 0)}
        </div>
      </CustomTooltip>
    );
  };

  const tableColumns = useMemo(() => {
    const completionColumns = [
          {
            title: 'Hoàn thành',
            dataIndex: 'hoanThanh',
            key: 'hoanThanh',
            align: 'center' as const,
            width: 120,
            render: (value: number | null | undefined, record: ColumnItem, index: number) => {
              if (hasChilds) {
                return renderHoanThanhResultCell(record);
              }
              // Lá: nhập/sửa thực tế.
              return renderHoanThanhInputCell(value, record, index, { disabled: false });
            },
          },
        ];

    return [
    {
      title: 'Kỳ báo cáo',
      dataIndex: 'date',
      key: 'date',
      fixed: 'left',
      align: 'left' as const,
      width: 120,
      render: (text: string, record: ColumnItem) => {
        return (
          <div style={{ width: '100%', wordBreak: 'break-word' }} className="leading-[1.2]">
            {!hasChilds ? <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{record.title}</div> : null}
            <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: hasChilds ? 5 : 0 }}>{text}</div>
          </div>
        );
      },
    },
    ...completionColumns,
    {
      title: 'Chỉ tiêu',
      dataIndex: 'chiTieu',
      key: 'chiTieu',
      align: 'center' as const,
      width: 120,
      render: (value: number) => <span style={{ fontWeight: 600, fontSize: 11, marginTop: 2 }}>{CommonJs.formatMoney(value) || '0'}</span>,
    },
    {
      title: 'Đơn vị',
      dataIndex: 'position',
      key: 'position',
      align: 'center' as const,
      width: 170,
      render: (text: string) => {
        const allUnitOptions = unitHelpers?.transformUnitsToOptions() || [];
        const unitOptions = allUnitOptions.filter((opt) => !unitHelpers?.isThangDoUnit(String(opt.value)));

        // Get current selected unit label
        const selectedUnitOption = unitOptions.find((opt) => opt.value === text);
        const tooltipText = selectedUnitOption ? selectedUnitOption.label : text || 'Chưa chọn đơn vị';
        // CustomTooltip chỉ hiển thị khi có prop `value` (không dùng `title` làm điều kiện)
        return (
          <CustomTooltip value={tooltipText} placement="top">
            <span className="block w-full min-w-0 cursor-default text-center text-[11px] font-semibold leading-[1.2] mt-2">
              {selectedUnitOption?.label || text || '-'}
            </span>
          </CustomTooltip>
        );

      },
    },
    {
      title: 'Tình trạng',
      dataIndex: 'trangThai',
      key: 'trangThai',
      align: 'center' as const,
      width: 170,
      render: (_: boolean, record: ColumnItem, index: number) => {
        const rowKey = getRowEditKey(record, index);
        const hasReported = hasReportedRef.current[rowKey] ?? false;
        const effectiveHoanThanh = hasReported
          ? (record.hoanThanh ?? hoanThanhDraftRef.current[rowKey] ?? 0)
          : 0;
        const trangThai = hasReported && effectiveHoanThanh >= (record.chiTieu ?? 0);

        return (
          <div className="flex items-center justify-center gap-1 text-[11px] mt-1">
            <Image
              src={`/icon/${trangThai ? 'up' : 'down'}.png`}
              height={14}
              alt={trangThai ? 'Đạt' : 'Không đạt'}
              title={trangThai ? 'Đạt' : 'Không đạt'}
              preview={false}
            />
            <span className={hasReported ? 'text-success' : 'text-danger'}>
              {hasReported ? 'Đã báo cáo' : 'Chưa báo cáo'}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Ghi chú',
      dataIndex: 'content',
      key: 'content',
      width: 170,
      render: (text: string, record: ColumnItem, index: number) => {
        const rowKey = getRowEditKey(record, index);
        if (hasChilds) {
          return (
            <div className="whitespace-pre-wrap break-words text-[11px]">{text ?? ''}</div>
          );
        }

        return (
          <div>
            <textarea
              key={rowKey}
              spellCheck={false}
              className="form-control"
              rows={1}
              style={{
                fontWeight: 'normal',
                resize: 'vertical',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                border: 'none',
                marginTop: 0,
                marginBottom: 0,
              }}
              defaultValue={text ?? ''}
              onChange={(e) => {
                const nextContent = e.target.value;
                contentDraftRef.current[rowKey] = nextContent;
                markRowAsEdited(record, index, undefined, nextContent);
              }}
              onBlur={(e) => {
                const nextContent = e.target.value;
                contentDraftRef.current[rowKey] = nextContent;

                setTableData((prev) => {
                  const updated = [...prev];
                  updated[index] = { ...updated[index], content: nextContent };
                  return updated;
                });

                markRowAsEdited(record, index, undefined, nextContent);
              }}
            />
          </div>
        );
      },
    },
    {
      title: 'Tài liệu',
      dataIndex: 'filesDinhKem',
      key: 'filesDinhKem',
      width: 170,
      align: 'center' as const,
      render: (files: string, record: ColumnItem) => {
        const fileArray = typeof files === 'string' ? files.split(',').filter((f) => f.trim()) : [];
        const fileCount = fileArray.length;
        const hasFiles = fileCount > 0;

        //if (hasChilds) return null;

        return (
          <Button
            size="small"
            icon={<FileOutlined />}
            style={{
              width: '100%',
              maxWidth: '110px',
              margin: '0 auto',
              cursor: 'pointer',
              backgroundColor: hasFiles ? '#5cb85c' : '#faad14',
              color: '#fff',
              fontWeight: 'bold',
              border: 'none', fontSize: '10px',
            }}
            onClick={() => {
              setSelectedRecordForUpload(record);
              setDocumentFiles(fileArray);
              setUploadModalVisible(true);
              setUploadModalOnlyView(hasChilds);
            }}
          >
            {fileCount > 0 ? `Có ${fileCount} file tài liệu` : 'Không có file'}
          </Button>
        );
      },
    },
    {
      title: (
        <div className="flex items-center" style={{ flexDirection: 'column', gap: 2 }}>
          <span>Thao tác</span>
          {editedRowKeys.size > 0 && !hasChilds ? (
            <Button
              type="primary"
              size="small"
              className="!h-[22px] !min-h-[22px] !px-2 !py-0 !text-[10px] !leading-tight"
              onClick={() => void handleSaveAllRef.current()}
              loading={saving}
              disabled={loading || saving || tableData.length === 0}
            >
              Lưu tất cả
            </Button>
          ) : null}
        </div>
      ),
      key: 'action',
      hidden: hasChilds,
      fixed: 'right',
      align: 'center' as const,
      width: 100,
      render: (_: unknown, record: ColumnItem, index: number) => {
        if (readOnly || hasChilds) return null;

        const isChanged =
          record.__edited === true || editedRowKeys.has(getRowEditKey(record, index));
        if (!isChanged) return null;

        return (
          <CustomTooltip value="Lưu">
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              style={{ minWidth: 34 }}
              onClick={() => void handleSaveRowRef.current(record, index)}
              loading={savingRowIndex === index}
            />
          </CustomTooltip>
        );
      },
    },
  ];
  }, [
    getRowEditKey,
    editedRowKeys,
    loading,
    markRowAsEdited,
    readOnly,
    saving,
    savingRowIndex,
    tableData.length,
    unitHelpers,
    hasChilds,
  ]);

  /** Virtual table bỏ qua re-render khi chỉ columns đổi — gắn cờ dirty vào row để dataSource đổi. */
  const tableDataWithEditFlag = useMemo(
    () =>
      tableData.map((row, index) => ({
        ...row,
        __edited: editedRowKeys.has(getRowEditKey(row, index)),
      })),
    [tableData, editedRowKeys, getRowEditKey]
  );

  // Virtual list dùng `components.body.wrapper` làm root scroll (div trong div).
  // Không được trả về <tbody> — HTML sai, ref không gắn → rc-virtual-list gọi addEventListener trên undefined.
  const tableComponents = useMemo(
    () => ({
      header: {
        wrapper: (p: TheadWrapperProps) => {
          const { className, ...rest } = p;
          return (
            <thead
              {...rest}
              className={[className, 'bg-gray-50 border-b border-[#e7eaf0] text-xs']
                .filter(Boolean)
                .join(' ')}
            />
          );
        },
      },
    }),
    []
  );


  return (
    <div className="w-full max-w-full min-w-0 overflow-x-hidden">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
        <div
          className="text-center"
          style={{
            padding: '6px 12px',
            backgroundColor: hasChilds ? 'rgba(92, 184, 92, 0.14)' : '#fffbe8',
            color: hasChilds ? '#229056' : '#8a6d3b',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 450,
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            marginTop: '6px',
            border: `1px solid ${hasChilds ? '#B7E1CD' : '#F5E7C3'}`,
          }}
        >
          {hasChilds ? (
            <>Báo cáo được tính từ các công việc con theo hình thức: {textHinhThuc}.</>
          ) : (
            <>Lưu ý: với báo cáo bằng 0, vui lòng nhập thêm ghi chú. </>
          )}
        </div>
      </div>

      {active ? (
        <div
          className="report-table-no-h-virtual-scroll w-full max-w-full min-w-0 overflow-x-hidden [&_.ant-table-wrapper]:max-w-full [&_.ant-table-wrapper]:overflow-x-hidden [&_.ant-table-container]:overflow-x-hidden [&_.ant-table-body]:overflow-x-hidden [&_.ant-table-tbody-virtual-holder]:overflow-x-hidden"
        >
          {/* Ant Design Table virtual: ẩn thanh scroll ngang tùy chỉnh (vẫn giữ scroll dọc). */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .report-table-no-h-virtual-scroll .ant-table-tbody-virtual-scrollbar-horizontal,
                .report-table-no-h-virtual-scroll .ant-table-tbody-virtual-scrollbar.ant-table-tbody-virtual-scrollbar-horizontal {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                  visibility: hidden !important;
                  pointer-events: none !important;
                }
              `,
            }}
          />
          <Table
            className="text-xs w-full max-w-full [&_.ant-table]:w-full [&_.ant-table-thead>tr>th]:!py-1.5 [&_.ant-table-thead>tr>th]:!px-2 [&_.ant-table-thead>tr>th]:!h-auto [&_.ant-table-thead>tr>th]:whitespace-normal [&_.ant-table-tbody>tr>td]:whitespace-normal [&_.ant-table-cell]:break-words"
            dataSource={tableDataWithEditFlag}
            columns={tableColumns as ColumnsType<ColumnItem>}
            rowKey={(record, index) => record.id || `row-${index}`}
            pagination={false}
            scroll={{ y: 590, x: 'max-content' }}
            tableLayout="fixed"
            components={tableComponents}
            loading={loading || saving}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "bg-gray-50" : "bg-white"
            }
            virtual
          />
        </div>
      ) : null}

      <UploadModal
        visible={uploadModalVisible}
        readOnly={uploadModalOnlyView}
        selectedRecord={selectedRecordForUpload}
        documentFiles={documentFiles}
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedRecordForUpload(null);
          setDocumentFiles([]);
        }}
        onSave={handleUploadModalSave}
      />
    </div>
  );
};

export interface ReportTableModalProps {
  open: boolean;
  onClose: () => void;
  data?: DuAnCongViecChiTietView | null;
  loading?: boolean;
  onSaveSuccess?: () => void;
  readOnly?: boolean;
  title?: string;
  width?: number | string;
  /** false → chỉ đóng bằng nút X (không đóng khi bấm nền / nhấn Esc). */
  closeOnOutside?: boolean;
  saveV2?: boolean;
}

/** Modal chỉ chứa báo cáo tiến độ (`getbaocao` trong `ReportTable` khi `active`). */
export const ReportTableModal: React.FC<ReportTableModalProps> = ({
  open,
  onClose,
  data,
  loading = false,
  onSaveSuccess,
  readOnly = false,
  title = 'Báo cáo tiến độ',
  width = 'min(1200px, 96vw)',
  closeOnOutside = true,
  saveV2 = false,
}) => (
  <Modal
    title={title}
    open={open}
    onCancel={onClose}
    maskClosable={closeOnOutside}
    keyboard={closeOnOutside}
    centered
    footer={null}
    width={width}
    destroyOnClose
    styles={{ body: { maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden' } }}
  >
    <Spin spinning={loading}>
      <ReportTable data={data} active={open} onSaveSuccess={onSaveSuccess} readOnly={readOnly} saveV2={saveV2} />
    </Spin>
  </Modal>
);

export default ReportTable;